import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  Pets as PetsIcon, CalendarToday as CalendarIcon,
  Receipt as InvoiceIcon, MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import { portalApi } from '../../services/portalApiClient';
import { API_ENDPOINTS } from '../../config/api';
import { useClientPortalStore } from '../../stores/clientPortalStore';

interface Pet { id: number; name: string; species: string; }
interface Appointment {
  id: number; appointment_time_start: string; pet_name: string; vet_name: string; appointment_type: string;
}
interface Invoice { id: number; balance_due: number; status: string; }

const PortalDashboardPage = () => {
  const navigate = useNavigate();
  const { client } = useClientPortalStore();
  const [pets, setPets] = useState<Pet[]>([]);
  const [nextAppt, setNextAppt] = useState<Appointment | null>(null);
  const [outstanding, setOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      portalApi.get<{ data: Pet[] }>(API_ENDPOINTS.PORTAL.PETS),
      portalApi.get<{ data: Appointment[] }>(`${API_ENDPOINTS.PORTAL.APPOINTMENTS}?status=upcoming`),
      portalApi.get<{ data: Invoice[] }>(API_ENDPOINTS.PORTAL.INVOICES),
    ]).then(([petsRes, apptRes, invRes]) => {
      setPets(Array.isArray(petsRes.data) ? petsRes.data : []);
      const appts = Array.isArray(apptRes.data) ? apptRes.data : [];
      setNextAppt(appts[0] ?? null);
      const inv = Array.isArray(invRes.data) ? invRes.data : [];
      setOutstanding(inv.reduce((s, i) => s + (i.balance_due ?? 0), 0));
    }).catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Welcome back, {client?.full_name?.split(' ')[0]}!
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>Here's a summary of your pets' care.</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardActionArea onClick={() => navigate('/client-portal/pets')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PetsIcon color="primary" sx={{ fontSize: 36 }} />
                <Typography variant="h4">{pets.length}</Typography>
                <Typography variant="body2" color="textSecondary">My Pets</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardActionArea onClick={() => navigate('/client-portal/appointments')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CalendarIcon color="info" sx={{ fontSize: 36 }} />
                <Typography variant="h4">{nextAppt ? 1 : 0}</Typography>
                <Typography variant="body2" color="textSecondary">Upcoming</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardActionArea onClick={() => navigate('/client-portal/invoices')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <InvoiceIcon color={outstanding > 0 ? 'error' : 'success'} sx={{ fontSize: 36 }} />
                <Typography variant="h4" color={outstanding > 0 ? 'error.main' : 'success.main'}>
                  ${outstanding.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">Outstanding</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardActionArea onClick={() => navigate('/client-portal/medical')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <MedicalIcon color="secondary" sx={{ fontSize: 36 }} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Medical Records</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {nextAppt && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="textSecondary">Next Appointment</Typography>
            <Typography variant="h6">{nextAppt.pet_name}</Typography>
            <Typography>{new Date(nextAppt.appointment_time_start).toLocaleString()}</Typography>
            <Typography color="textSecondary">With {nextAppt.vet_name || 'our team'}</Typography>
            {nextAppt.appointment_type && <Chip label={nextAppt.appointment_type} size="small" sx={{ mt: 1 }} />}
          </CardContent>
        </Card>
      )}

      {pets.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Your Pets</Typography>
          <Grid container spacing={2}>
            {pets.map(p => (
              <Grid item xs={12} sm={6} key={p.id}>
                <Card>
                  <CardActionArea onClick={() => navigate('/client-portal/pets')}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PetsIcon color="action" />
                      <Box>
                        <Typography fontWeight={600}>{p.name}</Typography>
                        <Typography variant="body2" color="textSecondary">{p.species}</Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default PortalDashboardPage;
